import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import SopralluogoStoryPreview from "./SopralluogoStoryPreview";

/**
 * HeroV2 — Mobile-first, mockup PRIMA del testo.
 *
 * iter v33 r68 · User: «mi hai lasciato ancora un sacco di testo, uno
 * non legge. Su mobile non funziona tanto bene. L'animazione del
 * sopralluogo si vede dopo del testo. Deve essere tutto piu' immediato».
 *
 * Fix radicale:
 *   • H1 da 4 righe → 2 righe massimo
 *   • Rimosso il pitch UPPERCASE separato + subtitle di 4 righe
 *   • Rimosso il badge App Store dentro l'hero (sposta in fondo)
 *   • Su mobile il MOCKUP viene renderizzato PRIMA del testo (order-1)
 *     cosi' l'animazione del sopralluogo e' subito visibile above-the-fold
 *   • Su desktop resta layout testo-a-sinistra, mockup-a-destra
 *   • 1 CTA principale, 1 secondaria testuale
 */

function HeroPhoneMockup({ compact = false }) {
  // iter v33 r70 · User: «forse troppo grande dal cellulare». Prop
  // `compact` riduce il mockup a 240px larghezza / 540px altezza su
  // mobile per liberare spazio all'headline sopra.
  const shellW = compact ? "w-[240px] sm:w-[300px]" : "w-[280px] sm:w-[320px]";
  const innerH = compact ? "h-[500px] sm:h-[660px]" : "h-[600px] sm:h-[720px]";
  return (
    <div className="flex justify-center" data-testid="hero-phone-mockup">
      <div
        className={`relative rounded-[44px] p-[3px] ${shellW}`}
        style={{
          background:
            "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
          boxShadow:
            "0 38px 80px -20px rgba(28,28,26,0.55), 0 14px 24px -10px rgba(28,28,26,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <div className={`bg-[#FAFAF7] rounded-[40px] overflow-hidden relative ${innerH}`}>
          <div className="absolute top-[14px] left-1/2 -translate-x-1/2 w-[100px] h-[26px] bg-black rounded-full z-20" />
          <div
            className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[5]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.06), transparent)",
            }}
          />
          <div className="px-5 pt-[44px] pb-1 flex items-center justify-between text-[11px] text-[#1C1C1A]">
            <span className="font-mono font-semibold">9:41</span>
            <span />
          </div>
          <SopralluogoStoryPreview />
        </div>
      </div>
    </div>
  );
}

export default function HeroV2() {
  const navigate = useNavigate();
  return (
    <section
      className="relative pt-6 pb-12 sm:pt-14 sm:pb-24 lg:pt-20 lg:pb-32 overflow-hidden bg-[#FAFAF7]"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 pointer-events-none opacity-60" aria-hidden>
        <div
          className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full blur-[140px]"
          style={{ background: "radial-gradient(circle, #7C8B6E40 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[140px]"
          style={{ background: "radial-gradient(circle, #B57C5E30 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">

        {/* iter v33 r70 · User: «non mi piace il GUARDA · dal vocale
            all'annuncio. Metterei subito il titolo, perciò Parli,
            scatti». Mobile-only mini-headline SOPRA il mockup: e' il
            titolo H1 breve e punchy, colpisce subito. Su desktop
            invece l'H1 lungo resta a sinistra del mockup (grid). */}
        <div className="lg:hidden text-center mb-4">
          <h1
            className="font-editorial text-[38px] leading-[0.98] tracking-tighter text-[#1C1C1A]"
            data-testid="hero-title-mobile"
          >
            Parli, scatti.
            <br />
            <span className="italic text-[#7C8B6E]">L&apos;annuncio &egrave; pronto.</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center">
          {/* MOCKUP · su mobile compatto per stare above-the-fold */}
          <div className="order-1 lg:order-2 lg:col-span-6">
            <div className="block lg:hidden">
              <HeroPhoneMockup compact />
            </div>
            <div className="hidden lg:block">
              <HeroPhoneMockup />
            </div>
          </div>

          {/* TESTO ESTESO · nasconde H1 duplicato su mobile (era gia' sopra),
              mostra invece direttamente il subtitle + CTA */}
          <div className="order-2 lg:order-1 lg:col-span-6 text-center lg:text-left">
            {/* H1 lungo · SOLO DESKTOP */}
            <h1
              className="hidden lg:block font-editorial text-6xl lg:text-[72px] leading-[0.98] tracking-tighter text-[#1C1C1A]"
              data-testid="hero-title"
            >
              Da un vocale e
              <br />
              qualche scatto veloce.
              <br />
              <span className="italic text-[#7C8B6E]">L&apos;annuncio</span>
              <br />
              <span className="italic text-[#7C8B6E]">&egrave; pronto.</span>
            </h1>

            <p
              className="mt-2 lg:mt-6 text-[15px] sm:text-[16px] lg:text-[18px] text-[#3F3D38] leading-snug max-w-md mx-auto lg:mx-0"
              data-testid="hero-subtitle"
            >
              Parli mentre giri casa, scatti qualche foto col telefono — <span className="text-[#1C1C1A] font-semibold">anche veloci, al buio, come vengono</span>.
              Pronto <span className="text-[#1C1C1A] font-semibold">migliora e riordina le foto</span> e in pochi minuti hai annuncio, social, sito personale <span className="text-[#1C1C1A] font-semibold">e tanto altro</span>.
              <span className="block mt-2 text-[13.5px] sm:text-[14px] lg:text-[15px] text-[#6B6A65] italic">
                Migliora foto mantiene la scena riconoscibile e ne cura la resa. Staging, Restyling e Render trasformano invece l&apos;immagine per mostrarne il potenziale.
              </span>
              <span className="block mt-1 text-[14px] sm:text-[15px] lg:text-[16px] text-[#1C1C1A] font-semibold">
                Vuoi solo sistemare le foto? Puoi fare anche solo quello.
              </span>
            </p>

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3">
              <button
                onClick={() => navigate("/register")}
                className="inline-flex items-center justify-center gap-2 bg-[#1C1C1A] hover:bg-black text-white px-6 py-3.5 rounded-xl font-semibold text-[15px] shadow-[0_8px_24px_-6px_rgba(28,28,26,0.28)] hover:shadow-[0_12px_32px_-6px_rgba(28,28,26,0.38)] transition-all active:scale-[0.98]"
                data-testid="hero-cta-primary"
              >
                Prova gratis
                <ArrowRight size={18} weight="bold" />
              </button>
              <button
                onClick={() => navigate("/prova")}
                className="inline-flex items-center justify-center gap-2 text-[#1C1C1A] hover:text-[#7C8B6E] px-4 py-3.5 rounded-xl font-medium text-[14px] underline underline-offset-4 decoration-[#7C8B6E]/40 hover:decoration-[#1C1C1A] transition-colors"
                data-testid="hero-cta-secondary"
              >
                Provala senza iscriverti
              </button>
            </div>

            {/* iter v33 r99g · Promo lancio: 60 crediti UNA TANTUM fino al
                30 settembre 2026. NON scrivere "60 crediti/mese" — sono
                un pacchetto gratuito una tantum del periodo promozionale. */}
            <div
              data-testid="hero-promo-banner"
              className="mt-5 sm:mt-6 inline-flex flex-col items-start gap-1.5 pl-4 pr-5 py-3 rounded-xl bg-[#F1F4ED] border border-[#7C8B6E]/25 max-w-md"
            >
              <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1C1C1A]">
                <CheckCircle size={14} weight="fill" className="text-[#7C8B6E]" />
                Prova ProntoImmo gratis
              </div>
              <div className="text-[12px] text-[#4a4a48] leading-relaxed pl-[22px]">
                60 crediti inclusi · nessuna carta richiesta
              </div>
              <div className="text-[11px] text-[#6B6A65] leading-relaxed pl-[22px]">
                Usali liberamente per provare tutte le funzioni.
              </div>
            </div>

            <div className="mt-4 sm:mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-x-4 gap-y-1.5 text-[12px] text-[#6B6A65]">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle size={13} weight="fill" className="text-[#7C8B6E]" />
                Senza carta
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle size={13} weight="fill" className="text-[#7C8B6E]" />
                Tu controlli il risultato
              </span>
            </div>

            {/* iter v33 r72 · User: «che sia chiaro che non devono per
                forza scaricare l'app iPhone, lo possono usare anche dal
                sito». Riga sopra il badge che spiega: funziona dal
                browser (Chrome, Safari, mobile o desktop) e IN PIÙ,
                se vuoi la comodità in tasca, c'è anche l'app iPhone. */}
            <div className="mt-6 flex flex-col items-center lg:items-start gap-2.5">
              <p
                className="text-[12.5px] sm:text-[13px] text-[#3F3D38] leading-snug text-center lg:text-left max-w-sm"
                data-testid="hero-web-first-hint"
              >
                <span className="font-semibold text-[#1C1C1A]">Funziona dal browser</span>, su computer o telefono.
                <span className="text-[#6B6A65]"> Non serve scaricare nulla.</span>
              </p>
              <div className="flex items-center gap-2.5">
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#9A968B]">
                  In più, se vuoi
                </span>
                <a
                  href="https://apps.apple.com/it/app/id6777127700"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Scarica Pronto su App Store"
                  data-testid="hero-app-store-badge"
                  className="inline-block transition-transform hover:scale-[1.03] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C8B6E]/40 rounded-lg"
                >
                  <img
                    src="/badges/app-store-it.svg"
                    alt="Scarica su App Store"
                    className="h-[38px] sm:h-[44px] w-auto"
                    draggable="false"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
